import logger from '@/utils/logger';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { QueryClient } from '@tanstack/react-query';
// Import types from shared package
import type { AuthUser } from '@teo/backend';
import { DEFAULT_IDLE_TIMEOUT, IDLE_TIMEOUT_MINUTES, idleTimeoutSchema } from '@teo/types';

import {
  AUTH_SESSION_INVALIDATED_EVENT,
  type AuthSessionInvalidatedDetail,
  adoptStoredAuthToken,
  classifyAuthToken,
  clearAuthTokens,
  clearAuthTokensIfOwn,
  hasAuthToken,
  invalidateAuthSession,
  isAuthTokenStorageEvent,
  rememberWorkosLogoutUrl,
  takeWorkosLogoutUrl,
} from '../lib/authSession';
import {
  clearAuthActivity,
  startActivityRecording,
  startInactivityTimeout,
} from '../lib/inactivityTimeout';
import { trpc } from '../lib/trpc';
import { applyLocalSignOut, applySessionInvalidation } from './authSessionInvalidation';

interface AuthContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  isLoading: boolean;
  logout: () => void;
}

/**
 * Authentication Context
 * Intent: Provide centralized user authentication state management
 * Handles login, registration, logout, and token management with expiration checking
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getInactivityTimeoutUserKey(user: AuthUser | null): string | null {
  return user?.displayId ?? null;
}

const MS_PER_MINUTE = 60_000;
let hasWarnedAboutIdleTimeout = false;

/**
 * How long this person may stay idle, in milliseconds. Null means no clock counts
 * down: nobody is signed in, or the person's timeout is `never`.
 *
 * The value is read through the schema, not trusted by its type. A server on
 * another version, or a stale copy of the user, can deliver a missing or unknown
 * value. That falls back to the default and never to a missing timer or NaN: a
 * timer that did not arm would leave the person signed in with no limit at all.
 */
function getInactivityTimeoutMs(user: AuthUser | null): number | null {
  if (user === null) return null;

  const parsed = idleTimeoutSchema.safeParse(user.idleTimeout);
  if (!parsed.success) {
    if (!hasWarnedAboutIdleTimeout) {
      hasWarnedAboutIdleTimeout = true;
      logger.warn(
        { received: String(user.idleTimeout), fallback: DEFAULT_IDLE_TIMEOUT },
        'User has no usable idle timeout; using the default',
      );
    }
    return IDLE_TIMEOUT_MINUTES[DEFAULT_IDLE_TIMEOUT] * MS_PER_MINUTE;
  }
  if (parsed.data === 'never') return null;
  return IDLE_TIMEOUT_MINUTES[parsed.data] * MS_PER_MINUTE;
}

function shouldArmInactivityTimeout(
  isLoading: boolean,
  inactivityTimeoutUserKey: string | null,
): boolean {
  return !isLoading && Boolean(inactivityTimeoutUserKey);
}

function handleInactivityTimeout(idleForMs: number, timeoutMs: number): void {
  invalidateAuthSession('inactivity-timeout', {
    idleForMs,
    timeoutMs,
  });
}

export function AuthProvider({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimeoutUserKey = getInactivityTimeoutUserKey(user);
  const inactivityTimeoutMs = getInactivityTimeoutMs(user);
  // The person the timer last counted for. The same person with a new timeout
  // resumes the clock. A different person starts a new one.
  const armedUserKeyRef = useRef<string | null>(null);
  // Whether a person is set, known at the moment it is set. A storage event can
  // arrive between the call and the render, and it must find the person there.
  const signedInRef = useRef(false);
  const setUser = useCallback((next: AuthUser | null) => {
    signedInRef.current = next !== null;
    setUserState(next);
  }, []);

  // Ends this tab's session and nothing that tabs share. A sign-out heard from
  // another tab is not this tab's to finish: the tokens, the activity stamp and
  // the WorkOS logout URL may by now belong to someone who signed in since.
  const endSessionInThisTab = useCallback(() => {
    // The clock is forgotten here, so the effect that sees the person go does not
    // remove the stamp in storage, as a sign-out of this tab's own does.
    armedUserKeyRef.current = null;
    applyLocalSignOut(queryClient, setUser);
  }, [queryClient, setUser]);

  useEffect(() => {
    const handleSessionInvalidated = (event: Event) => {
      const detail = (event as CustomEvent<AuthSessionInvalidatedDetail>).detail;

      logger.info(
        {
          reason: detail?.reason ?? 'unknown',
          navigateToLogin: detail?.navigateToLogin ?? false,
        },
        'AuthProvider received forced session invalidation event',
      );

      applySessionInvalidation(detail, queryClient, setUser);
    };

    window.addEventListener(
      AUTH_SESSION_INVALIDATED_EVENT,
      handleSessionInvalidated as EventListener,
    );

    return () => {
      window.removeEventListener(
        AUTH_SESSION_INVALIDATED_EVENT,
        handleSessionInvalidated as EventListener,
      );
    };
  }, [queryClient, setUser]);

  // Check for existing auth token on app load and establish who it belongs to.
  //
  // The server answers "who is this" for every token that can be used. A token
  // names who it was issued to, and a WorkOS one carries nothing else. What is
  // true of that person now, such as their profiles and their idle timeout, lives
  // in their row and can change after the token is minted. A copy of the user
  // inside a token is not read: a timer armed from it would keep the limit the
  // person had when they signed in. Nothing is armed while the answer is awaited.
  useEffect(() => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      setIsLoading(false);
      return;
    }

    const kind = classifyAuthToken(token);

    if (kind !== 'unusable') {
      let cancelled = false;
      // Asked for now, while the token is good, because the paths that need
      // it later have no token left: an idle timeout and a rejected refresh
      // both clear before they invalidate. Failure is not fatal — a session
      // that cannot be ended remotely still ends locally.
      // Only for a WorkOS session. A legacy or internal one lives nowhere but this
      // browser, so there is no vendor session to go and end.
      if (kind === 'workos') {
        void queryClient
          .fetchQuery(trpc.auth.workosLogoutUrl.queryOptions())
          .then((result) => {
            if (!cancelled && result.status === 'enabled') rememberWorkosLogoutUrl(result.url);
          })
          .catch(() => {
            logger.info('Could not learn where to end the WorkOS session');
          });
      }

      queryClient
        .fetchQuery(trpc.auth.getCurrentUser.queryOptions())
        .then(({ user: currentUser }) => {
          if (cancelled) return;
          // Another tab can sign out while this question is on its way. The answer is
          // then about a session that has ended, and the tab has no tokens left to
          // hold it with. Nothing is set, and the person is signed out here too.
          if (!hasAuthToken()) {
            queryClient.clear();
            return;
          }
          setUser(currentUser);
        })
        .catch(() => {
          if (cancelled) return;
          logger.info(`Session could not be resolved on mount (${kind})`);
          setUser(null);
          // The reason stays stable so it can be filtered on; which kind
          // failed is a dimension of it, not a different event. Naming WorkOS
          // in the reason would have quietly mislabelled every internal
          // session that failed here.
          clearAuthTokens('auth-provider-session-unresolved', { kind });
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    logger.info('Access token unusable on mount (malformed or wrong audience)');
    setUser(null);
    // Clear it so protected routes redirect once, here, and not after a request fails.
    clearAuthTokens('auth-provider-invalid-token-on-mount');
    setIsLoading(false);
  }, [queryClient, setUser]);

  // The deps are primitives, so a refreshed user with the same id and the same
  // timeout does not re-arm.
  useEffect(() => {
    if (inactivityTimeoutUserKey === null) {
      // A sign-out has a clock to forget. A page load with nobody signed in does
      // not: the stamp in storage belongs to another tab, and removing it would
      // sign that tab out.
      if (armedUserKeyRef.current !== null) {
        armedUserKeyRef.current = null;
        clearAuthActivity();
      }
      return;
    }
    if (!shouldArmInactivityTimeout(isLoading, inactivityTimeoutUserKey)) {
      return;
    }

    // A different person, whether they signed in or an impersonation switched to
    // them, starts a new clock by writing a new stamp. It replaces the stamp
    // instead of removing it, so no other tab ever sees the stamp missing.
    const resume = armedUserKeyRef.current === inactivityTimeoutUserKey;
    armedUserKeyRef.current = inactivityTimeoutUserKey;
    // The token in storage now belongs to the person this tab now holds.
    if (!resume) adoptStoredAuthToken();

    if (inactivityTimeoutMs === null) {
      return startActivityRecording({ resume });
    }

    return startInactivityTimeout({
      timeoutMs: inactivityTimeoutMs,
      resume,
      onTimeout: ({ idleForMs, timeoutMs, clockLost }) => {
        // Another tab's sign-out reached this tab first, and ended the session here.
        if (!signedInRef.current) return;

        if (!clockLost) {
          handleInactivityTimeout(idleForMs, timeoutMs);
          return;
        }
        // No activity could be found to count from. That is not idleness: another tab's
        // sign-out removes the stamp along with the tokens. The tokens are cleared only if
        // they are still this tab's own. Tokens that are gone, or newer, are not this
        // tab's to remove, and the tab then ends its own session and nothing more.
        if (clearAuthTokensIfOwn('inactivity-timeout', { idleForMs, timeoutMs })) {
          invalidateAuthSession(
            'inactivity-timeout',
            { idleForMs, timeoutMs },
            { clearTokens: false },
          );
        } else {
          endSessionInThisTab();
        }
      },
    });
  }, [inactivityTimeoutUserKey, inactivityTimeoutMs, isLoading, endSessionInThisTab]);

  // A sign-out in another tab removes the tokens that every tab shares. This tab
  // would go on showing the person, and a tab on never would show them until it is
  // reloaded, because it has no clock to end it. The tokens are what ends the
  // session, so the end is read from them and not from the idle timer.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (!signedInRef.current || !isAuthTokenStorageEvent(event)) return;
      // What is stored now decides, and not what the event says. A token stored since
      // is a refresh or a newer sign-in. After a refresh of this person the session
      // is not over. Whose token it is, is not checked, so after another person's
      // sign-in this tab still shows the person who left.
      if (hasAuthToken()) return;
      endSessionInThisTab();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [endSessionInThisTab]);

  /**
   * Ends the WorkOS session as well as ours, when there is one.
   *
   * Clearing our tokens alone is not a logout under hosted AuthKit: the
   * session lives in a cookie on WorkOS's domain, so the next sign-in is
   * handed a fresh code without anyone being asked for a password or a
   * passkey. Correct for SSO, wrong for someone who just pressed "log out".
   *
   * The URL is fetched *before* the tokens are cleared, because the procedure
   * that mints it is authenticated. If that fetch fails we still clear
   * locally — a logout must not be blocked by a network error, even at the
   * cost of leaving the vendor session alive.
   */
  const logout = () => {
    const finishLocally = () => {
      clearAuthTokens('user-initiated-logout');
      setUser(null);
      queryClient.clear();
    };

    const token = localStorage.getItem('authToken');
    if (!token || classifyAuthToken(token) !== 'workos') {
      // Nothing remote to end, but a URL remembered from an earlier WorkOS
      // session must not outlive it and send the next invalidation away.
      takeWorkosLogoutUrl();
      finishLocally();
      return;
    }

    void queryClient
      .fetchQuery(trpc.auth.workosLogoutUrl.queryOptions())
      .then((result) => {
        takeWorkosLogoutUrl();
        finishLocally();
        if (result.status === 'enabled') {
          // Leaves the SPA: WorkOS ends the session and redirects back to a
          // registered logout URI.
          window.location.assign(result.url);
        }
      })
      .catch(() => {
        // The remembered URL is the whole point of remembering it: a logout
        // that cannot reach us should still end the session at WorkOS.
        const remembered = takeWorkosLogoutUrl();
        finishLocally();
        if (remembered) {
          window.location.assign(remembered);
          return;
        }
        logger.info('Could not reach WorkOS to end the session; cleared locally');
      });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const __private = {
  getInactivityTimeoutMs,
  getInactivityTimeoutUserKey,
  handleInactivityTimeout,
  shouldArmInactivityTimeout,
};
